'use client';

import React, { useState } from 'react';

interface User {
  id: number;
  name: string;
  email: string;
  role: 'guest' | 'host' | 'both';
  status: 'active' | 'suspended' | 'pending';
  joined: string;
  bookings: number;
  location: string;
}

const users: User[] = [
  { id: 1, name: 'Sarah Nakato', email: 'sarah.nakato@email.com', role: 'guest', status: 'active', joined: 'Jan 2022', bookings: 6, location: 'Kampala' },
  { id: 2, name: 'James Ssekandi', email: 'james.s@email.com', role: 'host', status: 'active', joined: 'Mar 2021', bookings: 0, location: 'Kampala' },
  { id: 3, name: 'Grace Achieng', email: 'grace.a@email.com', role: 'both', status: 'active', joined: 'Jun 2022', bookings: 3, location: 'Entebbe' },
  { id: 4, name: 'David Ochieng', email: 'david.o@email.com', role: 'host', status: 'active', joined: 'Aug 2020', bookings: 0, location: 'Murchison' },
  { id: 5, name: 'Amina Hassan', email: 'amina.h@email.com', role: 'host', status: 'suspended', joined: 'Feb 2023', bookings: 0, location: 'Kampala' },
  { id: 6, name: 'Peter Mugisha', email: 'peter.m@email.com', role: 'guest', status: 'pending', joined: 'Aug 2026', bookings: 0, location: 'Jinja' },
  { id: 7, name: 'Ruth Kemigisha', email: 'ruth.k@email.com', role: 'host', status: 'active', joined: 'Nov 2021', bookings: 0, location: 'Kasese' },
  { id: 8, name: 'Moses Kato', email: 'moses.k@email.com', role: 'guest', status: 'active', joined: 'Apr 2023', bookings: 4, location: 'Kampala' },
];

const roleColors: Record<string, string> = {
  guest: 'bg-blue-100 text-blue-700',
  host: 'bg-purple-100 text-purple-700',
  both: 'bg-teal-100 text-teal-700',
};

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  suspended: 'bg-red-100 text-red-600',
  pending: 'bg-amber-100 text-amber-700',
};

export default function AdminUsers() {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const filtered = users.filter((u) => {
    const matchSearch = u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
    const matchRole = roleFilter === 'all' || u.role === roleFilter;
    const matchStatus = statusFilter === 'all' || u.status === statusFilter;
    return matchSearch && matchRole && matchStatus;
  });

  return (
    <div>
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Users', value: '14,832', color: 'text-gray-900' },
          { label: 'Active', value: '13,940', color: 'text-green-600' },
          { label: 'Hosts', value: '4,210', color: 'text-purple-600' },
          { label: 'Pending Verification', value: '142', color: 'text-amber-600' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-48">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40"
          />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40"
        >
          <option value="all">All Roles</option>
          <option value="guest">Guest</option>
          <option value="host">Host</option>
          <option value="both">Both</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4088C6]/40"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">User</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Role</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Location</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Joined</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((user) => (
                <tr key={user.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                        {user.name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                        <p className="text-xs text-gray-400">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${roleColors[user.role]}`}>{user.role}</span>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${statusColors[user.status]}`}>{user.status}</span>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-600">{user.location}</td>
                  <td className="px-5 py-4 text-sm text-gray-500">{user.joined}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2">
                      <button className="text-xs font-semibold text-rose-600 hover:text-rose-700 transition-colors">View</button>
                      <span className="text-gray-200">|</span>
                      {user.status === 'active' ? (
                        <button className="text-xs font-semibold text-red-500 hover:text-red-600 transition-colors">Suspend</button>
                      ) : (
                        <button className="text-xs font-semibold text-green-600 hover:text-green-700 transition-colors">Activate</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
